<script setup lang="ts">
import { PropType, toRef, watch } from 'vue';
import {
  Contexts,
  getContainer,
  NamedBindings,
  Helpers,
  TranslationService,
  ValuesSetValue,
  ContainerTypes,
} from '@dandelion/core';
import { ImporterService } from '../services/ImporterService';
import { ImportChoice } from '../entities/ImportChoice';

const props = defineProps({
  fullName: {
    type: String as PropType<string>,
    required: false,
    default: '',
  },
  context: {
    type: String as PropType<string>,
    required: false,
    default: Contexts.DEFAULT,
  },
  setItemGuid: {
    type: String as PropType<string>,
    required: false,
    default: '',
  },
  newVariable: {
    type: Object as PropType<any>,
    required: false,
    default: '',
  },
  oldVariable: {
    type: Object as PropType<any>,
    required: false,
    default: '',
  },
  containerType: {
    type: String as PropType<ContainerTypes>,
    required: false,
    default: () => ContainerTypes.DEFAULT,
  },
});

const container = getContainer(props.context);
const importerService = container.get('ImporterService') as ImporterService;
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;

const key: symbol = ImporterService.key;
const model = importerService.clonedModel;
const selectedValue = getRefByPath(props.fullName, toRef(model));
Helpers.setObjByPath(props.fullName, model[key], 'NotChosen');
const status = getRefByPath(props.fullName, toRef(model[key]));

watch(status, () => {
  switch (status.value) {
    case 'Existing':
      updateVariable(props.oldVariable);
      break;
    case 'Imported':
      updateVariable(props.newVariable);
      break;
    case 'NotChosen':
      updateVariable('');
      break;
  }
});

function processValue(value: any): string {
  if (value === '' || value === undefined || (typeof value === 'number' && isNaN(value))) {
    return ts.t('Empty value');
  }
  if (value instanceof ValuesSetValue) {
    return `<span class="value">${ts.t(value.getLabel())}</span>`;
  }
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item instanceof ValuesSetValue) {
          return `<span class="value">${ts.t(item.getLabel())}</span>`;
        } else if (typeof item === 'object' && item !== null) {
          return `<span>${processObject(item)}</span>`;
        } else {
          return `<span class="value">${ts.t(item)}</span>`;
        }
      })
      .join('');
  }
  if (typeof value === 'object' && value !== null) {
    return processObject(value);
  }
  if (typeof value === 'string' && value.match(/^\d{2}:\d{2}$/)) {
    return `<span class="value">${value}</span>`;
  }
  return `<span class="value">${value.toString()}</span>`;
}

function processObject(obj: any): string {
  return Object.keys(obj)
    .filter((key) => key !== 'guid')
    .map((key) => {
      const attr = obj[key];
      if (Array.isArray(attr)) {
        return attr
          .map(
            (item) =>
              `<span class="isArray">${key}: ${processValue(item)}</span>`
          )
          .join('');
      } else {
        return `<span class="attribute">${key}: ${processValue(attr)}</span>`;
      }
    })
    .join('');
}

function onValueChoice(newStatus: ImportChoice) {
  Helpers.setObjByPath(props.fullName, model[key], newStatus);
}

function onResetVariable() {
  Helpers.setObjByPath(props.fullName, model[key], 'NotChosen');
}

function updateVariable(selectedValue: string) {
  const existingVar = Helpers.getObjByPath(props.fullName, model);
  if (Array.isArray(existingVar)) {
    //Set
    if (
      existingVar.some((item) => typeof item === 'object' && 'guid' in item)
    ) {
      const existingItemIndex = existingVar.findIndex(
        (item) => item.guid === props.setItemGuid
      );
      if (existingItemIndex > -1) {
        if (selectedValue !== '') {
          Helpers.setObjByPath(
            `${props.fullName}[${existingItemIndex}]`,
            model,
            selectedValue
          );
        } else {
          existingVar.splice(existingItemIndex, 1);
          Helpers.setObjByPath(props.fullName, model, existingVar);
        }
      } else if (selectedValue !== '') {
        existingVar.push(selectedValue);
        Helpers.setObjByPath(props.fullName, model, existingVar);
      }
    } else {
      //Multiplechoice
      if (selectedValue !== '') {
        existingVar.splice(0, existingVar.length, ...selectedValue);
      }
      Helpers.setObjByPath(props.fullName, model, existingVar);
    }
  } else {
    Helpers.setObjByPath(props.fullName, model, selectedValue);
  }
}

function getRefByPath(path: string, obj: any) {
  return path.split('.').reduce((acc, key) => {
    const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
    if (arrayMatch) {
      const [, prop, index] = arrayMatch;
      return toRef(acc?.value[prop], Number(index));
    } else {
      return toRef(acc.value, key);
    }
  }, obj);
}
</script>

<template>
  <div
    :id="fullName + '-' + setItemGuid.substr(0, 6) + '-importer-hook'"
    class="importer-hook"
  >
    <div
      :class="{ 'selected-msg': true, 'is-displayed': status != 'NotChosen' }"
    >
      <span
        v-html="
          ts.t('You have selected the') +
          ' ' +
          ts.t(status == 'Existing' ? 'existing value' : 'imported value') +
          ':'
        "
      ></span>
      <div class="chosen-var" v-html="processValue(selectedValue)"></div>
      <div class="icon-wrapper" @click="onResetVariable()">
        <ion-icon name="refresh-outline"></ion-icon>
        <span class="tooltip" v-html="ts.t('Cancel the choice')"></span>
      </div>
    </div>
    <section
      :class="{ 'diff-section': true, 'is-hidden': status != 'NotChosen' }"
    >
      <span v-html="ts.t('Choose the value to import') + ':'"></span>
      <div class="values-wrapper">
        <div class="existingVariable">
          <div class="value-wrapper" v-html="processValue(oldVariable)"></div>
          <button
            class="secondary-button choose-existing-value-btn"
            :value="
              oldVariable instanceof Object
                ? JSON.stringify(oldVariable)
                : oldVariable
            "
            @click="onValueChoice('Existing')"
            v-html="ts.t('Maintain existing')"
            type="button"
          ></button>
        </div>
        <span class="arrow-right"></span>
        <div class="importedVariable">
          <div class="value-wrapper" v-html="processValue(newVariable)"></div>
          <button
            class="choose-imported-value-btn"
            :value="
              newVariable instanceof Object
                ? JSON.stringify(newVariable)
                : newVariable
            "
            @click="onValueChoice('Imported')"
            v-html="ts.t('Maintain imported')"
            type="button"
          ></button>
        </div>
      </div>
    </section>
  </div>
</template>

<style>
.diff-dialog .importer-hook {
  display: flex;
  align-items: center;
  position: relative;
  min-width: 20vw;
  transition: 1s ease;
}

.diff-dialog .existingVariable,
.diff-dialog .importedVariable {
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid #000;
  border-radius: 5px;
  text-align: center;
}

.diff-dialog .importer-hook button {
  margin-top: 12px;
}

.diff-dialog .arrow-right {
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 20px solid black;
}

.diff-dialog .importer-hook > *,
.diff-dialog .myProcedureList > * {
  white-space: nowrap;
}

.diff-dialog .importer-hook .diff-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  gap: 1.5rem;
  max-width: 80vw;
  max-height: 200vh;
  transition:
    max-height 1s ease-out,
    max-width 1s ease-out,
    1s linear;
  padding: 0 !important;
  margin-left: auto;
  margin-right: auto;
}

.diff-dialog .importer-hook .diff-section.is-hidden {
  opacity: 0;
  max-width: 0;
  max-height: 0;
  z-index: -1;
  transition:
    max-height 1s ease-out,
    max-width 1s ease-out,
    0.75s ease-out;
}

.diff-dialog .importer-hook .diff-section .values-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
}

.diff-dialog .diff-section .isArray {
  text-align: start;
}

.diff-dialog .diff-section .isArray span,
.diff-dialog .diff-section .value-wrapper span.attribute {
  display: flex;
  gap: 4px;
}

.diff-dialog .diff-section .value-wrapper span.attribute .value {
  white-space: nowrap;
  font-weight: 700;
}

.diff-dialog .diff-section .value-wrapper > .value {
  max-width: 15vw;
  white-space: break-spaces;
  font-weight: 700;
}

.diff-dialog .diff-section .isArray > span {
  padding-left: 10px;
}

.diff-dialog .importer-hook .selected-msg {
  position: absolute;
  display: flex;
  left: -25rem;
  align-items: center;
  padding: 1rem 1.5rem;
  color: #fff;
  background-color: #003354;
  border-radius: 10px;
  opacity: 0;
  z-index: -1;
  transition: 1s ease;
}

.diff-dialog .importer-hook .selected-msg.is-displayed {
  opacity: 1;
  z-index: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 10%);
  text-align: center;
}

.diff-dialog .importer-hook .selected-msg,
.diff-dialog .importer-hook .selected-msg span {
  height: min-content;
}

.diff-dialog .importer-hook .selected-msg .chosen-var {
  display: flex;
  max-width: 15vw;
  margin-left: 8px;
  padding: 0.5rem 0.8rem;
  background-color: #fff;
  border-radius: 5px;
  color: #003354;
}

.diff-dialog .importer-hook .selected-msg .chosen-var span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.diff-dialog .importer-hook .selected-msg ion-icon {
  position: relative;
  display: inline-block;
  padding: 0.5rem;
  background-color: #fff;
  font-size: 26px;
  font-weight: 800;
  color: #003354;
  border-radius: 5px;
  cursor: pointer;
}

.diff-dialog .importer-hook .icon-wrapper {
  position: relative;
  display: inline-block;
  margin-left: 1rem;
  height: 42px;
}

.diff-dialog .importer-hook .tooltip {
  background-color: #fff;
  border: 1px solid #003354;
  color: #003354;
  border-radius: 5px;
  padding: 0.3rem 0.5rem !important;
  position: absolute;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
}

.diff-dialog .importer-hook .tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #fff transparent transparent transparent;
}

.diff-dialog .importer-hook .icon-wrapper:hover .tooltip {
  opacity: 1;
}
</style>
